import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import axios from "axios";
import { connect } from "react-redux";
import { debounce } from "lodash";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Snackbar,
  Alert,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { useSwipeable } from "react-swipeable";

// Local resources
import { ChartComponent } from "./TVChart";
import { ErrorBoundary, LinearDeterminate } from "./Utils";
import { setCandleType, sethistLen, setHorizon } from "../actions/settings";

// button disable duration 1.2 sec
const cooldownDuration = 1200;

const horizonIntMap = {
  "1M": 30,
  "2M": 60,
  "3M": 90,
};

const defaultHorizon = 30;

const VoteApp = ({
  user_id,
  isAuthenticated,
  candleType,
  histLen,
  horizon,
}) => {
  const location = useLocation();
  sessionStorage.setItem("loginRedirect", location.pathname);

  const API_URL = process.env.REACT_APP_API_BASE_URL;

  const [formData, setValues] = useState({
    ticker: "SPY",
    priceData: [],
    volData: [],
    chartClose: 0,
  });

  const [loading, setLoading] = useState(false);
  const [showProgressBar, setShowProgressBar] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  // Fetch market data (debounced)
  const debouncedFetchData = useRef(
    debounce(async (params) => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_URL}/api/loadVoteData/`, { params });
        setValues({
          ticker: res.data.ticker,
          priceData: res.data.priceData,
          volData: res.data.volData,
          chartClose: res.data.chartClose,
        });
      } catch (err) {
        setAlert({
          open: true,
          message: "Failed to fetch data. Please try again later.",
          severity: "error",
        });
      } finally {
        setLoading(false);
        setShowProgressBar(false);
      }
    }, cooldownDuration)
  ).current;

  useEffect(() => {
    debouncedFetchData({ candleType, histLen, horizon });
    return () => {
      debouncedFetchData.cancel();
    };
  }, [candleType, histLen, horizon, debouncedFetchData]);

  useEffect(() => {
    const hasSeenInstructions = localStorage.getItem("hasSeenVoteInstructions");
    if (!hasSeenInstructions) {
      setShowInstructions(true);
    }
  }, []); // Empty dependency array ensures this runs only once on mount

  // Handle voting
  const handleVote = (vote) => {
    postVote(vote);
    setShowProgressBar(true);
    setTimeout(() => {
      handleNextChart();
    }, 1000);
  };

  // Save user vote
  const postVote = async (vote) => {
    try {
      const url = `${API_URL}/api/SaveVote/`;
      const voteInfo = {
        userid: user_id,
        ticker: formData.ticker,
        cast_close:
          Math.round((formData.chartClose + Number.EPSILON) * 100) / 100,
        cast_date: new Date().toISOString(),
        vote: vote,
        horizon: horizonIntMap[horizon] || defaultHorizon,
        count_close: -1,
        count_date: new Date().toISOString(),
      };
      await axios.post(url, voteInfo);
      setAlert({
        open: true,
        message: "Vote cast successfully!",
        severity: "success",
      });
    } catch (error) {
      setAlert({
        open: true,
        message: "Failed to save vote!",
        severity: "error",
      });
    } 
  };

  // Load next chart
  const handleNextChart = useCallback(() => {
    setShowProgressBar(true);
    debouncedFetchData({ candleType, histLen, horizon });
  }, [debouncedFetchData, candleType, histLen, horizon]);

  // Swipe handlers
  const swipeThreshold = Math.min(120, window.innerWidth * 0.1);
  const swipeHandlers = useSwipeable({
    onSwiping: ({ deltaX }) => {
      if (!isAnimating) {
        setSwipeOffset(deltaX);
      }
    },
    onSwiped: ({ dir, deltaX }) => {
      if (Math.abs(deltaX) > swipeThreshold) {
        if (dir === "Left") {
          setIsAnimating(true);
          setSwipeOffset(-window.innerWidth);
          setTimeout(() => {
            handleVote(-1);
            setSwipeOffset(0);
            setIsAnimating(false);
          }, 300);
        } else if (dir === "Right") {
          setIsAnimating(true);
          setSwipeOffset(window.innerWidth);
          setTimeout(() => {
            handleVote(1);
            setSwipeOffset(0);
            setIsAnimating(false);
          }, 300);
        }
      } else {
        setSwipeOffset(0); // snap back
      }
    },
    preventScrollOnSwipe: true,
    trackMouse: true,
  });

  const handleCloseInstructions = () => {
    setShowInstructions(false);
    localStorage.setItem("hasSeenVoteInstructions", "true");
  };

  if (!isAuthenticated) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        sx={{ height: "calc(100vh - 64px)", textAlign: "center", p: 2 }}
      >
        <Typography variant="h5" gutterBottom>
          Join the Community!
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Please log in or sign up to cast your vote. Voting results are used to
          calculate your reputation score.
        </Typography>
        <Box>
          <Button
            component={Link}
            to="/login"
            variant="contained"
            color="primary"
            sx={{ mr: 1 }}
          >
            Login
          </Button>
          <Button
            component={Link}
            to="/signup"
            variant="outlined"
            color="primary"
          >
            Sign Up
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        bgcolor: "#f5f5f5",
        height: "95vh",
        width: "100vw",
        m: 0,
        p: 0,
        overflow: "hidden",
      }}
    >
      <Box sx={{ flex: 1, position: "relative", width: "100%", height: "100%" }}>
        <Card
          sx={{
            height: "100%",
            bgcolor: "white",
            boxShadow: 0,
            borderRadius: 0,
            width: "100vw",
            transform: `translateX(${swipeOffset}px)`,
            transition: isAnimating ? "transform 0.3s ease" : "none",
          }}
          style={{ touchAction: "pan-y", overflow: "hidden", margin: 0, padding: 0 }}
          {...(!showProgressBar && !loading && !isAnimating ? swipeHandlers : {})}
        >
          <CardContent sx={{ p: 0, m: 0, height: "100%", width: "100%" }}>
            <ErrorBoundary>
              <div style={{ height: "100%", width: "100%", margin: 0, padding: 0 }}>
                {loading ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="100%"
                  >
                    <CircularProgress />
                  </Box>
                ) : showProgressBar ? (
                  <LinearDeterminate />
                ) : (
                  <ChartComponent
                    tickerSymbol={formData.ticker}
                    candleSeriesData={formData.priceData}
                    volumeSeriesData={formData.volData}
                  />
                )}
              </div>
            </ErrorBoundary>
          </CardContent>
        </Card>
        <Button
          size="small"
          variant="contained"
          onClick={handleNextChart}
          disabled={loading || showProgressBar || isAnimating}
          sx={{
            position: "absolute",
            top: 5,
            left: 5,
            bgcolor: "white",
            color: "black",
            zIndex: 10,
            "&:hover": { bgcolor: "#f0f0f0" },
          }}
        >
          Skip Chart
        </Button>

        {/* Instructions Dialog */}
        <Dialog open={showInstructions} onClose={handleCloseInstructions} aria-labelledby="instructions-dialog-title">
          <DialogTitle id="instructions-dialog-title">Welcome to the Voting Arena!</DialogTitle>
          <DialogContent dividers>
            <Typography gutterBottom>
              Predict the future price movement of S&P 500 stocks. Your votes help calculate your reputation score.
            </Typography>
            <Typography gutterBottom><strong>How to Vote:</strong></Typography>
            <ul>
              <li><Typography variant="body2"><strong>Swipe Right</strong> to vote "Up".</Typography></li>
              <li><Typography variant="body2"><strong>Swipe Left</strong> to vote "Down".</Typography></li>
              <li><Typography variant="body2">Use the <strong>Skip Chart</strong> button to get a new chart without voting.</Typography></li>
            </ul>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseInstructions} variant="contained">Got It!</Button>
          </DialogActions>
        </Dialog>

      </Box>
      <Snackbar
        open={alert.open}
        autoHideDuration={3000}
        onClose={() => setAlert({ ...alert, open: false })}
      >
        <Alert severity={alert.severity}>{alert.message}</Alert>
      </Snackbar>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  isAuthenticated: state.auth.isAuthenticated,
  user_id: state.auth.user_id,
  candleType: state.settings.candleType,
  histLen: state.settings.histLen,
  horizon: state.settings.horizon,
});

const mapDispatchToProps = { setCandleType, sethistLen, setHorizon };

export default connect(mapStateToProps, mapDispatchToProps)(VoteApp);
